import { isString } from 'fitify-types/src/type-guards/common'
import { isNullOrUndefined } from 'fitify-utils/src/properties'

import type { ClientErrorPayload } from '../types/error'

const IGNORED_URL_PATTERNS = ['chrome-extension://', 'moz-extension://']
const IGNORED_MESSAGES = ['Script error.']

class ErrorReporter {
  private userId?: string

  setUser(userId: string) {
    this.userId = userId
  }

  async report(error: unknown, message?: string) {
    if (this.isIgnoredError(error, message)) {
      return
    }

    const normalizedError =
      error instanceof Error ? error : new Error(this.stringify(error))

    const payload: ClientErrorPayload = {
      message: normalizedError.message,
      name: normalizedError.name,
      stack: normalizedError.stack,
      userId: this.userId,
      url: window.location.href,
      userAgent: navigator.userAgent,
    }

    try {
      await fetch('/api/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    } catch (error) {
      console.warn(`Could not report error`, error)
    }
  }

  private isIgnoredError(error: unknown, message?: string): boolean {
    const errorString =
      error instanceof Error
        ? `${error.message} ${error.stack ?? ''}`
        : this.stringify(error)

    if (IGNORED_URL_PATTERNS.some((pattern) => errorString.includes(pattern))) {
      return true
    }

    // Some browsers (like Chrome) report cross-origin script errors with a generic message and no stack trace
    if (isNullOrUndefined(error) && message) {
      return IGNORED_MESSAGES.some((pattern) => message === pattern)
    }

    return false
  }

  private stringify(value: unknown): string {
    if (isString(value)) {
      return value
    }
    if (isNullOrUndefined(value)) {
      return String(value)
    }
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
}

export const errorReporter = new ErrorReporter()
